import { IJobTitleState } from 'store/types';
import { IDaySegment, IPOSProvider, IPOSTotalWithUnallocated, ITipCalculatorPayee, ITipCalculatorResultCalculatedTotals, ITipCalculatorResultCategory, ITipCalculatorResultContribution, ITipCalculatorResultReceived } from 'types';

export enum CalculatorSteps {
  SELECT_DATE = 'select-date',
  REVIEW = 'review',
  POOLS = 'pools',
  OTHER_CONTRIBUTORS = 'other-contributors',
  CHECKS = 'checks',
  LABOR_DATA = 'labor-data',
  TIP_DATA = 'tip-data',
  CHANGE_LOG = 'change-log',
}

export interface ICalculatorPaymentForm {
  payees: ITipCalculatorPayee[];
  description: string;
  authorizedAgreement: boolean;
  paymentDate: string;
}

export interface ITipsBreakdown {
  name: string;
  jobTitle: string;
  /**
   * the number of points configured in the site's tip calculator
   * configuration for this job title
   *
   * Only used if calculatorMethod is TipCalculatorMethod.POINTS
   */
  points?: number;
  /**
   * the total number of minutes the employee worked for this job title
   * during this segment. if day segments are not enabled for this site,
   * this will be the total number of minutes the employee worked on this
   * date.
   */
  minutes: number;
  /**
   * the total amount (in cents) the employee is to be paid for this
   * job title during this segment. if day segments are not enabled for
   * this site, this will be the total amount the employee is to be paid
   * on this date.
   */
  payment?: number;
  /**
   * the totals breakdown returned from the POS.
   */
  totals?: IPOSTotalWithUnallocated;
  /**
   * the contributions paid by this employee for this job title during
   * this segment. if day segments are not enabled for this site, this
   * will be the total contributions paid by this employee on this date.
   */
  contributions?: ITipCalculatorResultContribution[];
  /**
   * the total amount this employee retained from tips they directly
   * received.
   */
  retained: ITipCalculatorResultCalculatedTotals;
  /**
   * an array of amounts this payee received indirectly (i.e. from
   * other employees contributions)
   */
  received: ITipCalculatorResultReceived[];
  /**
   * an array of amounts this payee revieved based on sales categories
   */
  categories?: ITipCalculatorResultCategory[];

  cash?: number;
  creditCard?: number;
  creditCardFee?: number;
  total?: number;
}

export enum EmployeeLaborJobErrorType {
  Other,
  InvalidData,
  TipCalcConfig,
}

export interface IEmployeeLaborJobError {
  field: 'daySegment' | 'jobTitle' | 'duration' | 'combo';
  message: string;
  type: EmployeeLaborJobErrorType;
}

export interface IEmployeeLaborJobState {
  daySegment: IDaySegment;
  jobTitle: IJobTitleState;
  duration?: number;
  total?: number;
  id: string;
  touched?: boolean; // true once the user has manually edited this job's duration
  error?: IEmployeeLaborJobError;
}

export interface ILaborDataPayee {
  id: string;
  name: string;
  labor: IEmployeeLaborJobState[];
  provider: IPOSProvider;
  hasSplitChecks: boolean;
  dataIndex: number;
  autoClockOut: boolean;
}

export interface ITipsBreakdownLineItem {
  label: string;
  description?: string | React.ReactNode;
  /**
   * the amount (in cents) that is to be held back from the employee's
   * payout
   *
   * set to null if holdbacks are disabled.
   */
  amount: number;
  subItems?: { label: string; amount: number }[];
}
